import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';
import { Customer, CustomerAddress, CustomerUpdateInput } from '@/models/customer.ts';
import { useSnackbar } from 'notistack';

interface CreateCustomerRequest {
    name: string;
    email: string;
    phone?: string;
    status?: string; // Default is 'active' in the database
    kyc_data?: Record<string, any>;
    settings?: Record<string, any>;
}

// List customers
export const useCustomers = (params?: {
    per_page?: number;
    page?: number;
    search?: string;
    sort_by?: string;
    sort_direction?: 'asc' | 'desc';
}) => {
    return useQuery<Customer[]>({
        queryKey: ['crm-customers', params],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<Customer[]>>(
                API_ENDPOINTS.CRM.CUSTOMERS.LIST,
                { params }
            );
            return data.data;
        },
    });
};

// Get customer by ID
export const useCustomer = (customerId: string) => {
    return useQuery<ApiResponse<Customer>>({
        queryKey: ['crm-customers', customerId],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<Customer>>(
                API_ENDPOINTS.CRM.CUSTOMERS.DETAILS(customerId)
            );
            return data;
        },
        enabled: !!customerId,
    });
};

// Create customer
export const useCreateCustomer = () => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<Customer>, Error, CreateCustomerRequest>({
        mutationFn: async data => {
            const response = await httpClient.post<ApiResponse<Customer>>(
                API_ENDPOINTS.CRM.CUSTOMERS.LIST,
                data
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['crm-customers'] });
            queryClient.invalidateQueries({ queryKey: ['customers'] });
        },
    });
};

// Update customer
export const useUpdateCustomer = (customerId: string) => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<Customer>, Error, CustomerUpdateInput>({
        mutationFn: async data => {
            const response = await httpClient.put<ApiResponse<Customer>>(
                API_ENDPOINTS.CRM.CUSTOMERS.DETAILS(customerId),
                data
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['crm-customers'] });
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            queryClient.invalidateQueries({ queryKey: ['crm-customers', customerId] });
        },
    });
};

export const useDeleteCustomer = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, string>({
        mutationFn: async customerId => {
            await httpClient.delete(API_ENDPOINTS.CRM.CUSTOMERS.DETAILS(customerId));
        },
        onSuccess: (_, customerId) => {
            queryClient.invalidateQueries({ queryKey: ['crm-customers'] });
            queryClient.invalidateQueries({ queryKey: ['crm-customers', customerId] });
            queryClient.invalidateQueries({ queryKey: ['customers'] });
        },
    });
};

// Customer Address Management

interface AddressRequest {
    type: 'billing' | 'shipping' | 'residential' | 'business';
    address_line_1: string;
    address_line_2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    is_default: boolean;
}

// Add address to customer
export const useAddCustomerAddress = (customerId: string) => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<CustomerAddress>, Error, AddressRequest>({
        mutationFn: async addressData => {
            const response = await httpClient.post<ApiResponse<CustomerAddress>>(
                API_ENDPOINTS.CRM.CUSTOMERS.ADDRESSES(customerId),
                addressData
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['crm-customers', customerId] });
        },
    });
};

// Update customer address
export const useUpdateCustomerAddress = (customerId: string, addressId: string) => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<CustomerAddress>, Error, Partial<AddressRequest>>({
        mutationFn: async addressData => {
            const response = await httpClient.put<ApiResponse<CustomerAddress>>(
                API_ENDPOINTS.CRM.CUSTOMERS.ADDRESS(customerId, addressId),
                addressData
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['crm-customers', customerId] });
        },
    });
};

// Delete customer address
export const useDeleteCustomerAddress = (customerId: string) => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, string>({
        mutationFn: async addressId => {
            await httpClient.delete(API_ENDPOINTS.CRM.CUSTOMERS.ADDRESS(customerId, addressId));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['crm-customers', customerId] });
        },
    });
};

// Reset customer password
export const useResetCustomerPassword = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async ({ customerId, password }: { customerId: string; password: string }) => {
            const { data } = await httpClient.post<ApiResponse>(
                API_ENDPOINTS.CRM.CUSTOMERS.RESET_PASSWORD(customerId),
                { password }
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['crm-customers'] });
            enqueueSnackbar('Customer password reset successfully', { variant: 'success' });
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to reset customer password', {
                variant: 'error',
            });
        },
    });
};

// Bulk update customer status
export const useBulkUpdateCustomerStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            customerIds,
            statusId,
        }: {
            customerIds: string[];
            statusId: string;
        }) => {
            const { data } = await httpClient.post<ApiResponse>(
                API_ENDPOINTS.CRM.CUSTOMERS.BULK_UPDATE_STATUS,
                { customer_ids: customerIds, status_id: statusId }
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            queryClient.invalidateQueries({ queryKey: ['crm-customers'] });
        },
    });
};

// Bulk assign customers
export const useBulkAssignCustomers = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async ({
            customerIds,
            assignedUserId,
        }: {
            customerIds: string[];
            assignedUserId: string | null;
        }) => {
            const { data } = await httpClient.post<ApiResponse>(
                API_ENDPOINTS.CRM.CUSTOMERS.BULK_ASSIGN,
                { customer_ids: customerIds, assigned_user_id: assignedUserId ?? null }
            );
            return data;
        },
        onSuccess: data => {
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            enqueueSnackbar(data.message || 'Customers assigned successfully', {
                variant: 'success',
            });
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to assign customers', {
                variant: 'error',
            });
        },
    });
};

// Bulk delete customers
export const useBulkDeleteCustomers = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async (customerIds: string[]) => {
            const { data } = await httpClient.post<ApiResponse>(
                API_ENDPOINTS.CRM.CUSTOMERS.BULK_DELETE,
                { customer_ids: customerIds }
            );
            return data;
        },
        onSuccess: data => {
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            enqueueSnackbar(data.message || 'Customers deleted successfully', {
                variant: 'success',
            });
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to delete customers', {
                variant: 'error',
            });
        },
    });
};
